import React, { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as growthActions from '../../../redux/actions/growthEngine';
import './Stepper.css';
import { StepOne } from './StepOne';
import { StepTwo } from './StepTwo';
import { StepThree } from './StepThree';
import { StepFour } from './StepFour';
import { StepFive } from './StepFive';
import { StepSix } from './StepSix';
import { FeatureFM } from './FeatureFM';
import { YoutubeVideos } from './YoutubeVideos';
import { YoutubeTarget } from './YoutubeTarget';
import AppTour from '../../../components/AppTour/AppTour';
import { growthEngineStepperSteps } from '../../../components/AppTour/ModuleTourSteps';

export default function StepperDemo() {
    const { hash } = useLocation();
    const dispatch = useDispatch();
    const [runTour, setRunTour] = useState(false);
    
    // Get Redux state for formData
    const { formData, currentStep } = useSelector(state => state.growthEngine);

    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    React.useEffect(() => {
        if (hash === "#start-tour") {
            setRunTour(true);
        }
    }, [hash]);

    // Initialize demo with dummy data if needed
    React.useEffect(() => {
        const dummyFormData = {
            objective: formData.objective || "More Streams",
            platformType: formData.platformType || "Facebook",
            shouldShowFeatureFM: formData.shouldShowFeatureFM !== undefined ? formData.shouldShowFeatureFM : true,
            metaAccessToken: formData.metaAccessToken || "mock_access_token",
            adAccountId: formData.adAccountId || "123456789",
            pageId: formData.pageId || "987654321",
            pageAccessToken: formData.pageAccessToken || "page_token",
            selectedChannel: formData.selectedChannel || "My Channel",
            budget: formData.budget || 100,
            startDate: formData.startDate || new Date(),
            endDate: formData.endDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
            callToAction: formData.callToAction || "Listen Now",
            adCaption: formData.adCaption || "Check out my new track!",
            destinationUrl: formData.destinationUrl || "https://spotify.com",
            adHeadline: formData.adHeadline || "New Music Release",
        };

        // Initialize with dummy data
        dispatch(growthActions.updateFormData(dummyFormData));
    }, [dispatch]);

    // Create demo steps dynamically based on selected objective
    const demoSteps = useMemo(() => {
        const steps = [];
        
        steps.push({
            id: 'stepOne',
            title: 'Step 1: Set your goal',
            component: <StepOne />,
            description: 'Choose your campaign objective from available options'
        });

        if (formData.objective) {
            steps.push({
                id: 'stepTwo',
                title: 'Step 2: Select Platform & Connect',
                component: <StepTwo />,
                description: 'Connect your platform account for the campaign'
            });

            if (formData.shouldShowFeatureFM) {
                steps.push({
                    id: 'featureFM',
                    title: 'Feature FM: Select Your Release',
                    component: <FeatureFM />,
                    description: 'Choose which music release to promote'
                });
            }

            if (formData.objective === "YouTube Growth") {
                steps.push({
                    id: 'youtubeVideo',
                    title: 'YouTube: Select Video to Promote',
                    component: <YoutubeVideos />,
                    description: 'Choose which video to promote'
                });

                steps.push({
                    id: 'youtubeTarget',
                    title: 'YouTube: Set Targeting',
                    component: <YoutubeTarget />,
                    description: 'Target audience for your YouTube campaign'
                });
            }

            steps.push({
                id: 'stepThree',
                title: 'Step 3: Budget & Schedule',
                component: <StepThree />,
                description: 'Set your budget and campaign duration'
            });

            steps.push({
                id: 'stepFour',
                title: 'Step 4: Target Audience',
                component: <StepFour />,
                description: 'Define your audience demographics and interests'
            });

            steps.push({
                id: 'stepFive',
                title: 'Step 5: Ad Creative',
                component: <StepFive />,
                description: 'Create your ad content and messaging'
            });

            steps.push({
                id: 'stepSix',
                title: 'Step 6: Review & Launch',
                component: <StepSix />,
                description: 'Review and confirm your campaign setup'
            });
        }

        return steps;
    }, [formData]);

    return (
        <div className="stepper-demo-page overflow-hidden" data-tour="stepper-demo">
            <AppTour
                tourSteps={growthEngineStepperSteps}
                runTour={runTour}
                setRunTour={setRunTour}
            />

            {/* Header */}
            <div className="sticky top-0 shadow z-40 bg-theme_clr1 text-white px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center">
                    <h1 className="text-lg font-medium">Create Campaign Steps Demo</h1>
                </div>
                <div className="text-sm text-white text-opacity-70">
                    All steps with mock data
                </div>
            </div>

            {/* Demo Content */}
            <div className="bg-gray-50 min-h-screen py-8">
                <div className="container max-w-6xl mx-auto px-4">
                    {/* Introduction */}
                    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                        <h2 className="text-2xl font-bold text-theme_clr1 mb-2">Campaign Setup Flow Demo</h2>
                        <p className="text-gray-600 mb-3">
                            This demo page displays all steps of the Growth Engine campaign setup process sequentially. Each section is self-contained and shows how the UI appears with different objectives.
                        </p>
                        <p className="text-sm text-gray-500">
                            Current Objective: <span className="font-semibold text-theme_clr1">{formData.objective || "Select an objective"}</span>
                        </p>
                    </div>

                    {/* Steps Display */}
                    {demoSteps.length > 0 ? (
                        demoSteps.map((step, index) => (
                            <div key={step.id} className="mb-8">
                                {/* Step Header */}
                                <div id="campaign-selector" data-tour="campaign-selector">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="flex items-center justify-center w-10 h-10 bg-theme_clr2 text-white rounded-full font-bold text-sm">
                                            {index + 1}
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold text-theme_clr1">{step.title}</h3>
                                            <p className="text-sm text-gray-600">{step.description}</p>
                                        </div>
                                    </div>

                                    {/* Step Content */}
                                    <div className="bg-gradient-to-b from-theme_clr1 to-theme_clr1/95 rounded-lg shadow-lg p-6 md:p-8 overflow-x-auto">
                                        <div className="min-h-96">
                                            {step.component}
                                        </div>
                                    </div>
                                </div>

                                {/* Divider */}
                                {index < demoSteps.length - 1 && (
                                    <div className="flex items-center gap-3 my-6">
                                        <div className="flex-1 h-1 bg-gradient-to-r from-theme_clr2 to-transparent rounded"></div>
                                        <div className="text-gray-400 text-sm font-medium">↓</div>
                                        <div className="flex-1 h-1 bg-gradient-to-l from-theme_clr2 to-transparent rounded"></div>
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className="bg-white rounded-lg shadow-md p-8 text-center">
                            <p className="text-gray-600 mb-2">Loading demo steps...</p>
                            <p className="text-sm text-gray-500">Please select an objective in Step 1</p>
                        </div>
                    )}

                    {/* Summary */}
                    {formData.objective && (
                        <div className="bg-white rounded-lg shadow-md p-6 mt-12" data-tour="summary">
                            <h3 className="text-xl font-bold text-theme_clr1 mb-4">Campaign Summary</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Objective</p>
                                    <p className="text-lg font-semibold text-theme_clr1">{formData.objective}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Platform</p>
                                    <p className="text-lg font-semibold text-theme_clr1">{formData.platformType || "Not selected"}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Budget</p>
                                    <p className="text-lg font-semibold text-theme_clr1">${formData.budget || 0}/day</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Call to Action</p>
                                    <p className="text-lg font-semibold text-theme_clr1">{formData.callToAction || "Not set"}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Footer Note */}
                    <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-800">
                            <strong>Note:</strong> This is a demo page showing all steps with mock data. Steps are displayed conditionally based on your objective selection. In the actual application, users navigate through these steps one at a time with validation at each stage.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
